import { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_UNIT } from './units';
import { DEFAULT_LOAD_RANGE } from './training/setEntry';

/**
 * The caller's unit and the load bounds expressed in it.
 *
 * Both come from the server. The unit governs how loads are interpreted on
 * write and rendered on read; the range lets the client validate inline without
 * owning a copy of the bounds — see units.js for why the client holds no
 * conversion of its own.
 *
 * Each caller gets its own fetch, deliberately. Train, EditProgram and
 * UnitPreference live on separate routes, so navigating between them unmounts
 * one and mounts the next, and the new mount reads the current preference. That
 * is what keeps them from disagreeing — if any screen is ever cached across
 * navigation, or two of these render at once, this must be hoisted into a
 * provider first. Two components holding different units is not a cosmetic bug:
 * a screen posting a load tagged with a stale unit stores a 2.2x error.
 *
 * A failed fetch is not surfaced as an error. The defaults are correct for the
 * overwhelming majority of users, the server validates regardless, and blocking
 * a training session on a preferences call would be a worse outcome than
 * rendering "kg" to someone who wanted "lb".
 */
export function usePreferences() {
  const { token } = useAuth();
  const [preferences, setPreferences] = useState({
    unit: DEFAULT_UNIT,
    availableUnits: [DEFAULT_UNIT],
    loadRange: DEFAULT_LOAD_RANGE,
    loaded: false,
  });

  useEffect(() => {
    if (!token) return undefined;

    // Guards against setting state after the component has gone, and against a
    // slow first response overwriting a faster second one.
    let active = true;

    axios
      .get(buildApiUrl('/preferences'), { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        if (!active) return;
        const data = response?.data ?? {};
        setPreferences({
          unit: data.unit || DEFAULT_UNIT,
          availableUnits: data.availableUnits?.length ? data.availableUnits : [DEFAULT_UNIT],
          loadRange: data.loadRange ?? DEFAULT_LOAD_RANGE,
          loaded: true,
        });
      })
      .catch(() => {
        if (!active) return;
        setPreferences((current) => ({ ...current, loaded: true }));
      });

    return () => {
      active = false;
    };
  }, [token]);

  return preferences;
}
